
import { computed, defineComponent, onBeforeMount, reactive, ref } from 'vue'

// @components
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import { CarrierT } from '@/core/types/carriers.types'
import { VehicleType } from '@/core/types/couriers.types'
import { FTLCarTypeT } from '@/core/types/ftl.types'

// @services
import { carriers } from '@/core/realisations/carriers.realisation'
import { FTL } from '@/core/realisations/FTL.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'

// @validation
import useVuelidate from '@vuelidate/core'
import { required, integer } from '@vuelidate/validators'

// @composable
import { useFormData } from '@/common/composable/useFormData'
import { SecurityGetCheckupStatusResponseT } from '@/core/types/security.types'
import { security } from '@/core/realisations/security.realisation'
import useNotifications from '@/common/composable/useNotifications'

// @constants

export default defineComponent({
    components: {
        NewModal,
        NewSelect,
        FileReader,
        NewInput,
        NewButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['create', 'cancel'],
    setup(_, { emit }) {
        const { getFormData } = useFormData()
        const notifications = useNotifications()

        const is_loading = ref(false)

        const state = reactive({
            number: null as string | null,
            model: null as string | null,
            company_id: null as number | null,
            vehicle_type_id: null as number | null,
            ftl_car_type_id: null as number | null,
            cubature: null as string | null,
            tech_passport_number: null as string | null,
            technicalPassport: [] as File[],
        })

        const sb_state_car = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
            //
            is_success: false,
        })

        const state_form$ = useVuelidate(
            {
                number: { required },
                model: { required },
                company_id: { required },
                vehicle_type_id: { required },
                ftl_car_type_id: { required },
                cubature: { required, integer },
            },
            state
        )

        const data_state = reactive({
            carriers: {
                is_loading: false,
                is_failed: false,
                list: [] as CarrierT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            vehicle_types: {
                is_loading: false,
                is_failed: false,
                list: [] as VehicleType[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            ftl_car_types: {
                is_loading: false,
                is_failed: false,
                list: [] as FTLCarTypeT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
        })

        const car_form$ = useVuelidate({ number: { required } }, state)

        //

        const resetSbStateCar = () => {
            sb_state_car.is_checked = false
            sb_state_car.is_rejected = null
            sb_state_car.reject_comment = null
            sb_state_car.is_request_completed = false
            sb_state_car.is_success = false
        }

        const checkSbStatusCar = async () => {
            car_form$.value.$touch()
            if (car_form$.value.$invalid || sb_state_car.is_loading || !state.number) {
                return
            }

            resetSbStateCar()
            try {
                sb_state_car.is_loading = true

                const AUTO_CHECKUP_TYPE = 3

                const response: SecurityGetCheckupStatusResponseT = await security.getCheckupStatus(
                    {
                        identifier: state.number as string,
                        type: AUTO_CHECKUP_TYPE,
                    }
                )

                sb_state_car.is_checked = true

                if (!response.data.isAccepted) {
                    sb_state_car.is_rejected = true
                    sb_state_car.reject_comment = response.data.comment

                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                } else {
                    sb_state_car.is_success = true
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state_car.is_checked = false
            } finally {
                sb_state_car.is_loading = false
                sb_state_car.is_request_completed = true
            }
        }

        //

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 50,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true
                const response = await carriers.getList(params)
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
            } catch (error) {
                data_state.carriers.is_failed = true
            } finally {
                data_state.carriers.is_loading = false
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 50,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true

                const response = await carriers.getList(params)
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
            } catch (error) {
                data_state.carriers.is_failed = true
            } finally {
                data_state.carriers.is_loading = false
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        //

        const loadVehicleTypes = async (page: number) => {
            data_state.vehicle_types.pagination.current_page = page

            try {
                data_state.vehicle_types.is_loading = true
                const response = await couriers.getVehicleTypes(page)
                data_state.vehicle_types.list = response.data

                data_state.vehicle_types.pagination.total_pages = response.meta.last_page

                data_state.vehicle_types.is_failed = false
            } catch (error) {
                data_state.vehicle_types.is_failed = true
            } finally {
                data_state.vehicle_types.is_loading = false
            }
        }

        const loadMoreVehicleTypes = async (page: number) => {
            if (page > data_state.vehicle_types.pagination.total_pages) return
            data_state.vehicle_types.pagination.current_page = page

            try {
                data_state.vehicle_types.is_loading = true

                const response = await couriers.getVehicleTypes(page)
                data_state.vehicle_types.list = data_state.vehicle_types.list.concat(response.data)

                data_state.vehicle_types.pagination.total_pages = response.meta.last_page

                data_state.vehicle_types.is_failed = false
            } catch (error) {
                data_state.vehicle_types.is_failed = true
            } finally {
                data_state.vehicle_types.is_loading = false
            }
        }

        //

        const loadFtlCarTypes = async () => {
            data_state.ftl_car_types.pagination.current_page = 1

            try {
                data_state.ftl_car_types.is_loading = true
                const response = await FTL.getCarTypes()

                data_state.ftl_car_types.list = response.data
                data_state.ftl_car_types.pagination.total_pages = 1

                data_state.ftl_car_types.is_failed = false
            } catch (error) {
                data_state.ftl_car_types.is_failed = true
            } finally {
                data_state.ftl_car_types.is_loading = false
            }
        }

        //

        const emitCreate = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || !sb_state_car.is_success) return

            emit('create', getFormData(state), state.technicalPassport)
        }

        const emitCancel = () => {
            emit('cancel')
        }

        // computed Car

        const isRejectedCar = computed(
            () => sb_state_car.is_rejected && sb_state_car.is_request_completed
        )
        const isNotCheckedCar = computed(
            () => !sb_state_car.is_checked && sb_state_car.is_request_completed
        )

        //

        onBeforeMount(() => {
            loadCarriers(1)
            loadVehicleTypes(1)
            loadFtlCarTypes()
        })

        //

        return {
            state,
            sb_state_car,
            state_form$,
            car_form$,
            //
            data_state,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            checkSbStatusCar,
            //
            loadVehicleTypes,
            loadMoreVehicleTypes,
            //
            emitCreate,
            emitCancel,
            //
            is_loading,
            // car computed
            isRejectedCar,
            isNotCheckedCar,
        }
    },
})
